import { motion } from 'framer-motion';
import { useMotionReduced } from '@/hooks/useMotionReduced';

/**
 * "The Pulse" — the Duty assistant's signature presence. A calm breathing core
 * with state-reactive rings. Lightweight SVG (no WebGL). Degrades to a static
 * state dot under prefers-reduced-motion.
 */
export type PulseState = 'idle' | 'listening' | 'analysing' | 'advising';

const STATE_LABEL: Record<PulseState, string> = {
  idle: 'Duty is watching',
  listening: 'Duty is listening',
  analysing: 'Duty is analysing',
  advising: 'Duty has an insight',
};

export function Pulse({ state = 'idle', size = 64 }: { state?: PulseState; size?: number }) {
  const reduced = useMotionReduced();
  const center = size / 2;

  // Ring timing/scale per state — faster + tighter as the assistant gets busier.
  const ringConfig: Record<PulseState, { duration: number; scale: number; rings: number }> = {
    idle: { duration: 4, scale: 1.6, rings: 2 },
    listening: { duration: 1.6, scale: 1.9, rings: 3 },
    analysing: { duration: 1, scale: 1.5, rings: 3 },
    advising: { duration: 2, scale: 2.1, rings: 2 },
  };
  const cfg = ringConfig[state];

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={STATE_LABEL[state]}
      className="relative inline-flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        {!reduced &&
          Array.from({ length: cfg.rings }).map((_, i) => (
            <motion.circle
              key={i}
              cx={center}
              cy={center}
              r={size * 0.14}
              fill="none"
              stroke="rgb(var(--c-accent))"
              strokeWidth={1.5}
              initial={{ scale: 1, opacity: 0.5 }}
              animate={{ scale: cfg.scale, opacity: 0 }}
              transition={{
                duration: cfg.duration,
                repeat: Infinity,
                ease: 'easeOut',
                delay: (i * cfg.duration) / cfg.rings,
              }}
              style={{ transformOrigin: 'center' }}
            />
          ))}
        {/* Core */}
        <motion.circle
          cx={center}
          cy={center}
          r={size * 0.16}
          fill="rgb(var(--c-accent))"
          animate={reduced ? undefined : { opacity: [0.7, 1, 0.7] }}
          transition={{ duration: cfg.duration, repeat: Infinity, ease: 'easeInOut' }}
          style={{ filter: 'drop-shadow(0 0 6px rgb(var(--c-accent) / 0.7))' }}
        />
      </svg>
    </div>
  );
}
